import React from 'react'
import { Text } from '@consta/uikit/Text'
import { Button } from '@consta/uikit/Button'
import classes from './Header.module.scss'
import { NavLink, useNavigate } from 'react-router-dom'
import { IconRing } from '@consta/icons/IconRing'
import { IconEye } from '@consta/icons/IconEye'
import { useState, useRef, useEffect } from 'react'
import { 
    useGetUserCommentQuery, 
    usePatchNotificationMutation 
} from '../../services/notoficationService'
import { IconSettings } from '@consta/icons/IconSettings'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { useLazyGetObjectByCommentQuery } from '../../services/commentService'

interface HeaderProps {
  onMenuToggle: () => void;
  isSidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, isSidebarCollapsed }) => {
    const navigate = useNavigate()
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
    const notificationRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);

    // Получаем уведомления
    const { data: notificationsData, isLoading, error, refetch } = useGetUserCommentQuery({})

    // Хук для пометки уведомления как прочитанного
    const [patchNotification] = usePatchNotificationMutation()

    // Хук для получения информации об объекте комментария
    const [getObjectByComment] = useLazyGetObjectByCommentQuery()

    // Закрытие по клику вне области
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notificationRef.current && 
                buttonRef.current &&
                !notificationRef.current.contains(event.target as Node) &&
                !buttonRef.current.contains(event.target as Node)) {
                setIsNotificationsOpen(false)
            }
        }

        if (isNotificationsOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isNotificationsOpen])

    // Функция для форматирования даты
    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return 'только что'
        if (diffMins < 60) return `${diffMins} мин назад`
        if (diffHours < 24) return `${diffHours} ч назад`
        if (diffDays < 7) return `${diffDays} дн назад`
        
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short'
        })
    }

    // Функция для получения текста уведомления по типу
    const getNotificationText = (notification: any) => {
        switch (notification.notification_type) {
            case 'new_comment':
                return '📝 Новый комментарий'
            case 'comment_reply':
                return '↪ Ответ на комментарий'
            case 'mention':
                return '👤 Вас упомянули'
            case 'system':
                return '⚙️ Системное уведомление'
            default:
                return '🔔 Уведомление'
        }
    }

    // Количество непрочитанных уведомлений
    const unreadCount = notificationsData?.notifications?.filter(n => !n.is_read).length || 0

    // Функция для пометки уведомления как прочитанного
    const markAsRead = async (notificationUuid: string) => {
        try {
            await patchNotification({
                uuid: notificationUuid,
                is_read: true
            }).unwrap()
            refetch()
        } catch (error) {
            console.error('Ошибка при пометке уведомления как прочитанного:', error)
        }
    }

    // Функция для пометки всех уведомлений как прочитанных
    const handleMarkAllAsRead = async () => {
        if (!notificationsData?.notifications) return
        
        try {
            const unreadNotifications = notificationsData.notifications.filter(n => !n.is_read)
            await Promise.all(
                unreadNotifications.map(notification => 
                    patchNotification({
                        uuid: notification.uuid,
                        is_read: true
                    }).unwrap()
                )
            )
            refetch()
        } catch (error) {
            console.error('Ошибка при пометке всех уведомлений как прочитанных:', error)
        }
    }

    // Обработчик клика на уведомление
    const handleNotificationClick = async (notification: any) => {
        if (!notification.is_read) {
            await markAsRead(notification.uuid)
        }
        
        // Получаем информацию об объекте комментария
        try {
            const objectData = await getObjectByComment({
                comment_uuid: notification.comment_uuid
            }).unwrap()

            //('Object data:', objectData)

            // Если есть well_uuid - переходим на страницу результатов
            if (objectData.well_uuid) {
                navigate(`/project/${objectData.project_uuid}/results`, {
                    state: { selectedWellUuid: objectData.well_uuid }
                })
            } 
            // Если только project_uuid - переходим на страницу проекта
            else if (objectData.project_uuid) {
                navigate(`/project/${objectData.project_uuid}`)
            } 
            // Если нет данных - остаемся на текущей странице
            else {
                console.warn('Не удалось определить объект для комментария')
            }
        } catch (error) {
            console.error('Ошибка при получении информации об объекте:', error)
            // В случае ошибки остаемся на текущей странице
        }
        
        setIsNotificationsOpen(false)
    }

    return (
        <div className={classes.header}>
            <div className={classes.leftSection}>
                {/* Кнопка меню с тремя полосками */}
                <button 
                    className={`${classes.menuButton} ${isSidebarCollapsed ? '' : classes.expanded}`}
                    onClick={onMenuToggle}
                >
                    <div className={classes.menuBar}></div>
                    <div className={classes.menuBar}></div>
                    <div className={classes.menuBar}></div>
                </button>

                {/* Логотип */}
                <NavLink className={classes.logo} to='/'>
                    <Text className={classes.logoText}>Геоиндикатор</Text>
                </NavLink>
            </div>

            <div className={classes.buttons}>
                {/* Уведомления */}
                <button
                    ref={buttonRef}
                    className={`${classes.notificationButton} ${isNotificationsOpen ? classes.active : ''}`}
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                >
                    {/*@ts-ignore */}
                    <IconRing size="m" />
                    {unreadCount > 0 && (
                        <div className={classes.notificationBadge}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </div>
                    )}
                </button>

                {userInfo?.role?.access_level == 3 && 
                    <Button iconLeft={IconSettings} size='s' view='clear' onClick={() => window.open(process.env.REACT_APP_ADMIN_URL || 'https://geoindicator.ru/platform/api/admin/', '_blank')}/>
                }

                {isNotificationsOpen && (
                    <>
                        <div className={classes.overlay} />
                        <div ref={notificationRef} className={classes.notificationsPopup}>
                            <div className={classes.notificationsHeader}>
                                <div className={classes.headerTitle}>
                                    <Text size="l" weight="bold">Уведомления</Text>
                                    {unreadCount > 0 && (
                                        <Text size="s" view="brand" weight="semibold">
                                            {unreadCount} новых
                                        </Text>
                                    )}
                                </div>
                                {unreadCount > 0 && (
                                    <div className={classes.headerActions}>
                                        <button 
                                            className={classes.actionButton}
                                            onClick={handleMarkAllAsRead}
                                        >
                                            {/*@ts-ignore */}
                                            <IconEye size="xs" style={{ marginRight: '4px' }} />
                                            Прочитать все
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={classes.notificationsContainer}>
                                {notificationsData?.notifications && notificationsData.notifications.length > 0 ? (
                                    notificationsData.notifications.map((notification) => (
                                        <div
                                            key={notification.uuid}
                                            className={`${classes.notificationItem} ${!notification.is_read ? classes.unread : ''}`}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <div className={classes.notificationHeader}>
                                                <div className={classes.notificationTitle}>
                                                    <Text className={classes.notificationType}>
                                                        {getNotificationText(notification)}
                                                    </Text>
                                                </div>
                                                <Text className={classes.notificationTime}>
                                                    {formatDate(notification.created_at)}
                                                </Text>
                                            </div>
                                            
                                            <Text className={classes.notificationBody}>
                                                {notification.comment_body}
                                            </Text>
                                            
                                            <div className={classes.notificationMeta}>
                                                <Text className={classes.notificationAuthor}>
                                                    {notification.comment_author_name}
                                                </Text>
                                                {notification.object_name && (
                                                    <Text className={classes.notificationObject}>
                                                        {notification.object_name}
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className={classes.emptyNotifications}>
                                        <div className={classes.emptyIcon}>
                                            🔔
                                        </div>
                                        <Text className={classes.emptyTitle}>
                                            Уведомлений пока нет
                                        </Text>
                                        <Text className={classes.emptyText}>
                                            Здесь будут появляться новые уведомления о комментариях и действиях в системе
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <Button size='s' label='Личный кабинет' onClick={() => navigate('/account')} />
            </div>
        </div>
    )
}

export default Header